import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { merge, Observable, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { IItemComment } from '../../../pages/items/items-modal/item-comment/item-comment.interface';
import { IItemBase } from '../../../pages/items/items.interface';
import { IBoard } from '../../../pages/settings/boards/boards.interface';
import { BaseDatatableStore, IBaseDatatableState } from '../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../state/state.interface';
import { HttpUtilitiesService, IGetManyResponse } from '../../../utilities/http-utilities.service';

export interface ISearchResult {
  action: {
    currentPage: number;
    results: IItemBase[];
    total: number;
  };
  boards: {
    currentPage: number;
    results: IBoard[];
    total: number;
  };
  issue: {
    currentPage: number;
    results: IItemBase[];
    total: number;
  };
  itemComment: {
    currentPage: number;
    results: IItemComment[];
    total: number;
  };
  itemValue: {
    currentPage: number;
    results: IItemBase[];
    total: number;
  };
  itemDescription: {
    currentPage: number;
    results: IItemBase[];
    total: number;
  };
}

export interface IGlobalSearchState extends IBaseDatatableState<any> {
  searchFields: ISearchResult;
  searchStatus: ELoadStatus;
}

const initialSearchState: Pick<IGlobalSearchState, 'searchFields' | 'searchStatus'> = {
  searchFields: {
    action: { currentPage: 1, results: [], total: 0 },
    boards: { currentPage: 1, results: [], total: 0 },
    issue: { currentPage: 1, results: [], total: 0 },
    itemComment: { currentPage: 1, results: [], total: 0 },
    itemDescription: { currentPage: 1, results: [], total: 0 },
    itemValue: { currentPage: 1, results: [], total: 0 },
  },
  searchStatus: ELoadStatus.initial,
} as const;

@Injectable()
export class GlobalSearchStore extends BaseDatatableStore<any, IGlobalSearchState> {
  private readonly searchRequests = {
    action: (params: HttpParams): Observable<IGetManyResponse<IItemBase>> =>
      this.http.get<IGetManyResponse<IItemBase>>('items', { params }),
    boards: (params: HttpParams): Observable<IGetManyResponse<IBoard>> =>
      this.http.get<IGetManyResponse<IBoard>>('boards', { params }),
    issue: (params: HttpParams): Observable<IGetManyResponse<IItemBase>> =>
      this.http.get<IGetManyResponse<IItemBase>>('items', { params }),
    itemComment: (params: HttpParams): Observable<IGetManyResponse<IItemComment>> =>
      this.http.get<IGetManyResponse<any>>('item-comments', { params }),
    itemValue: (params: HttpParams): Observable<IGetManyResponse<IItemBase>> =>
      this.http.get<IGetManyResponse<IItemBase>>('items', { params }),
    itemDescription: (params: HttpParams): Observable<IGetManyResponse<IItemBase>> =>
      this.http.get<IGetManyResponse<IItemBase>>('items', { params }),
  } as const;

  public readonly isLoadMoreAvailableForActions$ = this.select((state) => state.searchFields.action).pipe(
    map((searchData) => searchData.total > searchData.results.length),
  );
  public readonly isLoadMoreAvailableForBoards$ = this.select((state) => state.searchFields.boards).pipe(
    map((searchData) => searchData.total > searchData.results.length),
  );
  public readonly isLoadMoreAvailableForIssues$ = this.select((state) => state.searchFields.issue).pipe(
    map((searchData) => searchData.total > searchData.results.length),
  );
  public readonly isLoadMoreAvailableForItemValues$ = this.select((state) => state.searchFields.itemValue).pipe(
    map((searchData) => searchData.total > searchData.results.length),
  );
  public readonly isLoadMoreAvailableForItemDescriptions$ = this.select(
    (state) => state.searchFields.itemDescription,
  ).pipe(map((searchData) => searchData.total > searchData.results.length));

  public readonly searchStatus$ = this.select((state) => state.searchStatus);
  public readonly searchFields$ = this.select((state) => state.searchFields);

  // Fields that are searched in parallel on every query.
  private static readonly SEARCHED_FIELDS: (keyof ISearchResult)[] = [
    'action',
    'boards',
    'issue',
    'itemComment',
    'itemDescription',
    'itemValue',
  ];

  readonly loadInitialSearch = this.effect((trigger$: Observable<string>) =>
    trigger$.pipe(
      switchMap((searchText: string) => {
        this.patchState((state: IGlobalSearchState) => ({
          ...state,
          searchFields: {
            action: { ...state.searchFields.action, currentPage: 1 },
            boards: { ...state.searchFields.boards, currentPage: 1 },
            issue: { ...state.searchFields.issue, currentPage: 1 },
            itemComment: { ...state.searchFields.itemComment, currentPage: 1 },
            itemDescription: { ...state.searchFields.itemDescription, currentPage: 1 },
            itemValue: { ...state.searchFields.itemValue, currentPage: 1 },
          },
          searchStatus: ELoadStatus.loading,
        }));

        return merge(
          ...GlobalSearchStore.SEARCHED_FIELDS.map((field) =>
            (
              this.searchRequests[field](this.getHttpParamsForLoadMore(searchText, field)) as Observable<
                IGetManyResponse<unknown>
              >
            ).pipe(
              tapResponse(
                (response) => this.applySearchFieldResult(field, response),
                // eslint-disable-next-line no-console
                (error) => console.error('Error loading data', error),
              ),
            ),
          ),
        ).pipe(tap({ complete: () => this.patchState({ searchStatus: ELoadStatus.success }) }));
      }),
    ),
  );

  readonly loadMoreSearchResults = this.effect(
    (trigger$: Observable<{ field: keyof ISearchResult; searchText: string }>) =>
      trigger$.pipe(
        switchMap(({ searchText, field }) => {
          let newPage = 1;
          this.patchState((state) => {
            newPage = state.searchFields[field].currentPage + 1;

            return {
              ...state,
              searchFields: {
                ...state.searchFields,
                [field]: { ...state.searchFields[field], currentPage: newPage },
              },
              searchStatus: ELoadStatus.loading,
            };
          });

          const params: HttpParams = this.getHttpParamsForLoadMore(searchText, field, newPage);

          return (this.searchRequests[field](params) as Observable<IGetManyResponse<unknown>>).pipe(
            tapResponse(
              (response) =>
                this.patchState((state) => ({
                  ...state,
                  searchFields: {
                    ...state.searchFields,
                    [field]: {
                      ...state.searchFields[field],
                      results: [...state.searchFields[field].results, ...response.data],
                      total: response.total,
                    },
                  },
                  searchStatus: ELoadStatus.success,
                })),
              // eslint-disable-next-line no-console
              (error) => console.error('Error loading data', error),
            ),
          );
        }),
      ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      ...initialSearchState,
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      singleCrudLoading: ELoadStatus.initial,
    });
  }

  private applySearchFieldResult(field: keyof ISearchResult, response: IGetManyResponse<unknown>): void {
    const results =
      field === 'itemComment'
        ? this.getUniqueCommentsWithCount((response.data as IItemComment[]) || [])
        : (response.data ?? []);

    this.patchState((state) => ({
      ...state,
      searchFields: {
        ...state.searchFields,
        [field]: {
          currentPage: 1,
          results,
          total: response.total!,
        },
      },
    }));
  }

  private getUniqueCommentsWithCount(comments: IItemComment[]): IItemComment[] {
    return Object.values(
      comments.reduce(
        (acc, current) => {
          const itemId: number | undefined = current?.item?.id;

          if (!itemId) {
            acc[Math.random()] = { ...current, commentCount: 1 };

            return acc;
          }

          if (!acc[itemId]) {
            acc[itemId] = { ...current, commentCount: 1 };
          } else {
            acc[itemId].commentCount = (acc[itemId].commentCount || 1) + 1;
          }

          return acc;
        },
        {} as Record<string, IItemComment>,
      ),
    );
  }

  private getHttpParamsForLoadMore(searchText: string, field: keyof ISearchResult, newPage = 1): HttpParams {
    if (field === 'boards') {
      return this.httpUtilities.insertGenericCrudRequestParameters({
        limit: 10,
        page: newPage,
        search: { searchedFields: ['name', 'key'], searchText },
      });
    }

    if (field === 'itemComment') {
      return this.httpUtilities.insertGenericCrudRequestParameters({
        fields: ['description'],
        join: ['item'],
        limit: 10,
        page: newPage,
        search: { searchedFields: ['description'], searchText },
      });
    }

    if (field === 'itemDescription') {
      return this.httpUtilities.insertGenericCrudRequestParameters({
        fields: ['name', 'key', 'description'],
        join: ['boardItemConfiguration||id'],
        limit: 10,
        page: newPage,
        search: { searchedFields: ['description'], searchText },
      });
    }

    if (field === 'itemValue') {
      return this.httpUtilities
        .insertGenericCrudRequestParameters({
          fields: ['name', 'key', 'values'],
          join: ['boardItemConfiguration||id'],
          limit: 10,
          page: newPage,
        })
        .set('valueSearch', searchText);
    }

    return this.httpUtilities.insertGenericCrudRequestParameters({
      fields: ['name', 'key'],
      filters: [{ field: 'boardItemConfiguration.itemType', ids: [field] }],
      join: ['boardItemConfiguration||id'],
      limit: 10,
      page: newPage,
      search: { searchedFields: ['name', 'key'], searchText },
    });
  }
}
